/**
 * DayTimeSlider — Dual-handle range slider for workflow time windows.
 *
 * Range: 0–86400 seconds (24 hours). Snaps to 5-minute increments.
 * Supports midnight crossing (start > end, e.g. 10 PM – 6 AM).
 * Displays time in 12-hour format via convertSecto12Hour.
 *
 * Props:
 *   start    — start time in seconds (0–86400)
 *   end      — end time in seconds (0–86400)
 *   setStart — setter for start value
 *   setEnd   — setter for end value
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { convertSecto12Hour } from '../../../../libs.jsx';

const MAX_TIME = 86400;
const SNAP = 300; // 5-minute increments

const TICKS = [0, 21600, 43200, 64800, 86400];

const DayTimeSlider = ({ start = 0, end = 86400, setStart, setEnd }) => {
    const [dragging, setDragging] = useState(null);
    const trackRef = useRef(null);

    const snap = (val) => Math.round(val / SNAP) * SNAP;

    const getTimeFromEvent = useCallback((e) => {
        if (!trackRef.current) return 0;
        const rect = trackRef.current.getBoundingClientRect();
        const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        return snap(Math.round(pct * MAX_TIME));
    }, []);

    const handleMouseDown = (handle, e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(handle);
    };

    useEffect(() => {
        if (!dragging) return;

        const onMove = (e) => {
            const time = getTimeFromEvent(e);
            // Allow free movement — no clamping — so handles can cross
            // This enables midnight-crossing windows (e.g. 10 PM – 6 AM)
            if (dragging === 'start') {
                setStart(time);
            } else {
                setEnd(time);
            }
        };

        const onUp = () => setDragging(null);

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        return () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
    }, [dragging, start, end, setStart, setEnd, getTimeFromEvent]);

    const startPct = (start / MAX_TIME) * 100;
    const endPct = (end / MAX_TIME) * 100;
    const crossesMidnight = end < start;

    // Build the track fill — wraps around when crossing midnight
    const getTrackStyle = () => {
        if (crossesMidnight) {
            return {
                background: `linear-gradient(to right,
                    var(--color-primary, #2563eb) 0%, var(--color-primary, #2563eb) ${endPct}%,
                    var(--color-bg-tertiary, #e5e7eb) ${endPct}%, var(--color-bg-tertiary, #e5e7eb) ${startPct}%,
                    var(--color-primary, #2563eb) ${startPct}%, var(--color-primary, #2563eb) 100%)`,
            };
        }
        return {};
    };

    // Time display helper — show 11:59 PM for 86400
    const formatTime = (time) => {
        if (time === MAX_TIME) return '11:59 PM';
        return convertSecto12Hour(time);
    };

    // Descriptive label
    const getLabel = () => {
        if (start === 0 && end === MAX_TIME) return 'All Day';
        if (crossesMidnight) return `${formatTime(start)} – ${formatTime(end)} (Crosses Midnight)`;
        return `${formatTime(start)} – ${formatTime(end)}`;
    };

    return (
        <div className="cf-slider">
            <div className="cf-slider__labels">
                <span>{getLabel()}</span>
            </div>
            <div
                className="cf-slider__track"
                ref={trackRef}
                style={getTrackStyle()}
            >
                {/* Normal fill bar — only used when NOT crossing midnight */}
                {!crossesMidnight && (
                    <div
                        className="cf-slider__fill"
                        style={{ left: `${startPct}%`, width: `${endPct - startPct}%` }}
                    />
                )}
                <div
                    className="cf-slider__handle"
                    style={{ left: `${startPct}%` }}
                    onMouseDown={(e) => handleMouseDown('start', e)}
                    role="slider"
                    aria-label="Start time"
                    aria-valuenow={start}
                    aria-valuemin={0}
                    aria-valuemax={MAX_TIME}
                    tabIndex={0}
                />
                <div
                    className="cf-slider__handle"
                    style={{ left: `${endPct}%` }}
                    onMouseDown={(e) => handleMouseDown('end', e)}
                    role="slider"
                    aria-label="End time"
                    aria-valuenow={end}
                    aria-valuemin={0}
                    aria-valuemax={MAX_TIME}
                    tabIndex={0}
                />
            </div>
            <div className="cf-slider__ticks">
                {TICKS.map((t) => (
                    <span key={t}>{convertSecto12Hour(t)}</span>
                ))}
            </div>
        </div>
    );
};

export default DayTimeSlider;
